import Api, { PluginConstructor, LoadOpts } from '../common/Api';
import ContentConfig, { MaybeContentConfig } from '../config/ContentConfig';
import EventEmitter from '../common/EventEmitter';
import PlayerType from '../player/PlayerType';
import extractJWTPayload from '../common/utils/extractJWTPayload';
import getBaseConfigFromBackend from './getBaseConfigFromBackend';
import getPlugin from './getPlugin';
import loadScript from './loadScript';
import logger from '../log';
import { ApiLogger } from '../log/Logger';
import {
  EventData,
  Plugin,
  KollectiveGlobalApi,
  SourceSetOptions
} from '../common/interfaces';
import { Events, InternalEvents } from '../events';
import {
  FetchFactoryOptions,
  FlowplayerOptions,
  Handler,
  HLSjsConfigWrapper,
  KollectiveConstructor,
  PlayerAPI,
  ViblastVideoJsPlayer,
  ViblastOptions,
  VideoJSOptions,
  VideoJSPlayer,
  XHRFactoryOptions,
  KollectiveLoadOptions
} from '@kollective-frontend/ksdk-api';
import {
  dispatchError,
  dispatchConfigUpdate,
  dispatchSourceSetEvent
} from '../events/dispatch';
import { TechApi } from './tech';
import { getEnvSuffix } from './util';
import configureReportLogging from '../common/configureReportLogging';
import validateBrowser from './validateBrowser';
import { GetSdkUriResponse } from '../api/getSdkUri';
import base64ToJson from '../common/utils/base64ToJson';

const log = logger.getLogger(__filename);
export const ksdkScriptId = 'ksdk::Script';

const {
  Bitmovin,
  FetchFactoryPlayer,
  XHRFactoryPlayer,
  Flowplayer,
  HLSjs,
  HLSjsConfig,
  Shaka,
  THEOplayer,
  Viblast,
  VideoJS
} = PlayerType;

const api = new Api();
const { PLAYER_READY } = InternalEvents;

declare global {
  interface Window {
    ksdk: KollectiveConstructor;
    Ksdk?: PluginConstructor;
  }
}

interface JITC {
  ah: string;
  tenantId: string;
  app: string;
}

export class Bootloader implements Plugin {
  hasLoadStarted?: boolean;
  isFinishedLoading?: boolean;
  playerType: string;
  player: PlayerAPI;
  playerPromise: Promise<EventData>;
  playerOptions?: unknown;
  log: ApiLogger;
  private readonly _tech: TechApi;
  private eventsToBind: Map<string, Handler<unknown>[]>;
  private config: ContentConfig;

  constructor(playerType: string, player: PlayerAPI, playerOptions?: unknown) {
    this._tech = ksdk._internal.tech;
    this.playerType = playerType;
    this.player = player;
    this.playerOptions = playerOptions;
    this.log = log;
    this.eventsToBind = new Map();
    this.config = ContentConfig.getInstance();
    this.playerPromise = new Promise(resolve => {
      EventEmitter.getInstance().addEventListener(PLAYER_READY, async event => {
        resolve(event.data);
      });
    });
  }

  mergeConfig(config: MaybeContentConfig): void {
    const plugin = getPlugin();
    if (plugin && typeof plugin.mergeConfig === 'function') {
      plugin.mergeConfig(config);
    }
    this.config.merge(config);
  }

  async load(opts: LoadOpts = {}): Promise<Plugin> {
    this.hasLoadStarted = true;
    const validationError = validateBrowser();
    if (validationError) {
      api.setDisabled();
      // eslint-disable-next-line no-console
      console.warn(validationError);
      this.isFinishedLoading = true;
      return handleError(validationError) as Plugin;
    }
    const emitter = EventEmitter.getInstance();
    let config: GetSdkUriResponse = {};

    if (opts.contentToken) {
      const token = opts.contentToken;
      const contentJwt = extractJWTPayload<{
        iss: string;
        ah: string;
        tid: string;
      }>(opts.contentToken);
      // if its an empty object, it's invalid
      if (!contentJwt || Object.keys(contentJwt).length == 0) {
        emitter.emit(PLAYER_READY, { data: {} });
        const reason = 'Invalid contentToken - Could not decode token.';
        this.isFinishedLoading = true;
        return handleError(reason) as Plugin;
      }
      const envHostSuffix = getEnvSuffix(contentJwt.iss, 'content.');
      config = await getBaseConfigFromBackend({
        apiHost: contentJwt.ah,
        tenantId: contentJwt.tid,
        token,
        envHostSuffix,
        overrideUrl: ((opts.config as MaybeContentConfig) || {}).sdkUrl
      });
    } else if (opts.jitc && opts.integratorToken) {
      const conf = base64ToJson(opts.jitc, 'Failed to decode jitc') as JITC;

      if (!conf.ah || !conf.tenantId) {
        this.isFinishedLoading = true;
        emitter.emit(PLAYER_READY, { data: {} });
        const reason = 'Invalid jitc - no api host or tenant id.';
        return handleError(reason) as Plugin;
      }
      const envHostSuffix = getEnvSuffix(conf.ah, 'api.');
      config = await getBaseConfigFromBackend({
        apiHost: conf.ah,
        tenantId: conf.tenantId,
        token: opts.integratorToken,
        envHostSuffix,
        overrideUrl: ((opts.config as MaybeContentConfig) || {}).sdkUrl
      });
    } else {
      this.isFinishedLoading = true;
      emitter.emit(PLAYER_READY, { data: {} });
      return handleError(
        'Invalid options - no content token, jit configuration and integrator token'
      ) as Plugin;
    }

    if (opts.config) {
      this.config.merge(opts.config, { from: 'bootloader.opts' });
    }
    this.config.merge(config, { from: 'bootloader' });
    opts.config = this.config;
    configureReportLogging();
    opts.disabled = config.disabled;
    if (config.disabled) {
      this.isFinishedLoading = true;
      return handleError('ksdk is disabled') as Plugin;
    }
    if (!config.sdkUrl) {
      this.isFinishedLoading = true;
      return handleError('ksdk url is not available.') as Plugin;
    }
    try {
      await loadScript(config.sdkUrl, ksdkScriptId, document.body);
    } catch (e) {
      log.error('Failed to load sdk script', e);
    }
    if (window.Ksdk) {
      const inst = new window.Ksdk(
        this.playerType,
        this.player,
        this.playerOptions,
        this._tech
      );
      api.setPlugin(inst);
      // rebind events before load is called.
      this.eventsToBind.forEach((value, key) => {
        value.forEach(v => {
          // Need to Cleanup preparation handlers
          EventEmitter.getInstance().removeEventListener(key as Events, v);
          inst.addEventListener(key, v);
        });
      });
      this.eventsToBind.clear();
      dispatchConfigUpdate(opts.config);
      this.mergeConfig(opts.config);
      const options = opts as KollectiveLoadOptions;
      this.player = await inst.load(options).then(i => i.getPlayer());
      emitter.emit(PLAYER_READY, { data: { item: inst } });
      return Promise.resolve(this);
    } else {
      this.isFinishedLoading = true;
      emitter.emit(PLAYER_READY, { data: {} });
      return handleError('ksdk is not available on the window.') as Plugin;
    }
  }

  addEventListener(name: string, handler: Handler<unknown>): void {
    const plugin = getPlugin();
    if (plugin) {
      plugin.addEventListener(name, handler);
    } else {
      // Need to be able to report events happening before plugin is initialized.
      EventEmitter.getInstance().addEventListener(name as Events, handler);
      const hs = this.eventsToBind.get(name);
      if (hs) {
        this.eventsToBind.set(name, [...hs, handler]);
      } else {
        this.eventsToBind.set(name, [handler]);
      }
    }
  }

  getConfig(): ContentConfig {
    return ContentConfig.getCopy();
  }

  isEcdnUsed(): boolean {
    const result = getPlugin()?.isEcdnUsed() ?? false;
    return this.isFinishedLoading ? result : true;
  }

  async getPlayer(): Promise<PlayerAPI> {
    if (!this.hasLoadStarted) {
      return handleError('getPlayer called before load') as PPAPI;
    }
    return this.playerPromise.then((item: EventData) => {
      if (item.item) {
        return item.item.getPlayer();
      } else {
        return handleError('unable to retrieve player from plugin') as PPAPI;
      }
    });
  }

  async sourceSet(url: string, options?: SourceSetOptions): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      dispatchSourceSetEvent({ url, options, resolve, reject });
    });
  }

  stop(): void {
    const internal = (window.ksdk as KollectiveGlobalApi)._internal;
    if (internal && internal.plugin) {
      // We are resetting the plugin, cleanup old state.
      internal.plugin.stop();

      internal.plugin = undefined;
    }
    EventEmitter.getInstance().clearEvents();
  }
}

type PPAPI = Promise<PlayerAPI>;
const handleError = (reason: string): unknown => {
  dispatchError(reason);
  return Promise.reject(new Error(reason));
};

const create = (
  playerType: string,
  player: PlayerAPI,
  playerOptions?: unknown
): Bootloader => {
  return new Bootloader(playerType, player, playerOptions);
};

function ksdk(
  playerType: string,
  player: PlayerAPI,
  playerOptions: unknown
): Bootloader {
  return create(playerType, player, playerOptions);
}

ksdk.forBitmovin = function (playerOptions: unknown): Bootloader {
  return create(Bitmovin, undefined, playerOptions);
};

ksdk.forViblast = function (options: ViblastOptions): Bootloader {
  return create(Viblast, {} as unknown as ViblastVideoJsPlayer, options);
};

ksdk.forViblastVideoJS = function (player: ViblastVideoJsPlayer): Bootloader {
  return create(Viblast, player);
};

ksdk.forVideoJS = function (
  player: VideoJSPlayer,
  playerOptions: VideoJSOptions
): Bootloader {
  return create(VideoJS, player, playerOptions);
};

ksdk.forShaka = function (player: PlayerAPI): Bootloader {
  return create(Shaka, player);
};

ksdk.forFlowplayer = function (
  player: PlayerAPI,
  options?: FlowplayerOptions
): Bootloader {
  return create(Flowplayer, player, options);
};

ksdk.forHLSjs = function (
  videoElement: HTMLVideoElement,
  hlsConfig: unknown
): Bootloader {
  return create(HLSjs, videoElement, hlsConfig);
};

ksdk.forHLSjsConfig = (config: HLSjsConfigWrapper): Bootloader => {
  return create(HLSjsConfig, config);
};

ksdk.forTHEOplayer = function (player: PlayerAPI): Bootloader {
  return create(THEOplayer, player);
};

ksdk.forFetchFactory = (options: FetchFactoryOptions): Bootloader => {
  return create(FetchFactoryPlayer, options);
};

ksdk.forXHRFactory = (options: XHRFactoryOptions): Bootloader => {
  return create(XHRFactoryPlayer, options);
};

ksdk._internal = { tech: new TechApi() };
ksdk.api = api;
window.ksdk = ksdk as unknown as KollectiveGlobalApi;

export default ksdk;
